import {
	reactive,
	onMounted,
	watch,
} from '@vue/composition-api';
import { setImmediate } from 'core-js';

const useMultipleSelect = (props, context = {}, options = {}) => {
	const { doms } = options;
	const { emit } = context;

	const state = reactive({
		isCheckedAll: props.checkAll || false,
		items: props.items || [],
		checkedIds: props.checkedIds || [],
		disabledSelectAll: props.disabledSelectAll,
	});

	// Local Functions
	const setIndeterminate = (indeterminate) => {
		const element = doms.checkAllRef.value.$el;
		element.querySelector('input').indeterminate = indeterminate;
	};

	const getIndeterminate = () => {
		const element = doms.checkAllRef.value.$el;
		return element.querySelector('input').indeterminate;
	};

	const addCheckboxId = (id) => {
		if (!state.checkedIds.includes(id)) {
			state.checkedIds.push(id);
		}
	};

	const removeCheckboxId = (id) => {
		const findIndex = state.checkedIds.findIndex((value) => value === id);
		if (findIndex >= 0) {
			state.checkedIds.splice(findIndex, 1);
		}
	};

	const setCheckboxValue = (isChecked = false, id) => {
		if (isChecked) {
			addCheckboxId(id);
		} else {
			removeCheckboxId(id);
		}
	};

	const setCheckboxesValue = (isChecked = false) => {
		state.items.forEach((_, index) => {
			state.items[index].value = isChecked;
			setCheckboxValue(isChecked, state.items[index].id);
		});
	};

	const setCheckboxesByIds = (isChecked = false, ids = []) => {
		state.items.forEach((_, index) => {
			ids.forEach((id) => {
				if (state.items[index].id === id) {
					state.items[index].value = isChecked;
				}
			});
		});
	};

	// Handler Functions
	const handleChecked = (isChecked = false, index = 0) => {
		state.items[index].value = isChecked;
		setCheckboxValue(isChecked, state.items[index].id);
		emit('update:checked-ids', state.checkedIds);
	};

	const handleCheckedAll = (isChecked = false) => {
		state.isCheckedAll = isChecked;
		emit('update:checked-ids', state.checkedIds);
	};

	const handleWatchIsCheckedAll = (isCheckedAll) => {
		const isIndeterminate = getIndeterminate();

		if (isCheckedAll) {
			setCheckboxesValue(true);
		} else if (isCheckedAll === false && isIndeterminate === false) {
			setCheckboxesValue(false);

			state.checkedIds = [];
			emit('update:checked-ids', state.checkedIds);
		}

		if (state.disabledSelectAll) {
			emit('update:check-all', false);
		} else {
			emit('update:check-all', state.isCheckedAll);
		}
	};

	const handleWatchCheckboxes = (items) => {
		const isAllChecked = items.every((item) => item.value === true);
		const isOneChecked = state.items.some((checkbox) => checkbox.value === true);

		if (isAllChecked && !state.disabledSelectAll) {
			setIndeterminate(false);
			state.isCheckedAll = true;
		} else if (isOneChecked) {
			setIndeterminate(true);
			state.isCheckedAll = false;
		} else {
			setIndeterminate(null);
			state.isCheckedAll = false;
		}

		const isChecked = true;
		setCheckboxesByIds(isChecked, props.checkedIds);
	};

	const handleWatchPropCheckAll = (checkAll) => {
		state.isCheckedAll = checkAll;
	};

	onMounted(() => {
		if (state.isCheckedAll) {
			setCheckboxesValue(true);
		}

		if (props.checkedIds && props.checkedIds.length) {
			const isChecked = true;
			setCheckboxesByIds(isChecked, props.checkedIds);
		}
	});

	const handlePropItems = (newItems) => {
		state.items = newItems;

		const isIndeterminate = getIndeterminate();
		if (state.isCheckedAll && !isIndeterminate) {
			const isChecked = true;
			handleWatchIsCheckedAll(isChecked);
		} else if (state.checkedIds.length) {
			setTimeout(() => {
				setImmediate(true);
			}, 100);
		}
	};

	const handlePropCheckedIds = (newCheckedIds) => {
		if (newCheckedIds.length === 0) {
			setCheckboxesValue(false);
			setIndeterminate(null);
			state.checkedIds = [];
		} else {
			const isChecked = true;
			setCheckboxesByIds(isChecked, props.checkedIds);
		}
	};

	const handlePropDisabledSelectAll = (newDisabledSelectAll) => {
		state.disabledSelectAll = newDisabledSelectAll;
	};

	// when checkbox `check all value` was changed
	watch(() => state.isCheckedAll, handleWatchIsCheckedAll);

	// when items `data in array` was changed
	watch(() => state.items, handleWatchCheckboxes, { deep: true });

	// when `prop check all` was changed
	watch(() => props.checkAll, handleWatchPropCheckAll);

	// when `prop items` was changed
	watch(() => props.items, handlePropItems);

	// when `prop checkedIds`was changed
	watch(() => props.checkedIds, handlePropCheckedIds);

	// when `prop disabledSelectAll`was changed
	watch(() => props.disabledSelectAll, handlePropDisabledSelectAll);

	return {
		state,
		handleChecked,
		handleCheckedAll,
	};
};

export default useMultipleSelect;